// Owner console navigation: the 13 sections of IA §3, each mapped to its canonical ID. Phase 4
// rendered honest placeholders; D-065 fills Overview and Vendors and adds Plans & pricing next to
// Wallet and billing (the money sections sit right under Vendors).
import type { PlainKey } from "@ady/i18n";
import type { IconName } from "@ady/ui";

export interface ConsoleSection {
  id: string;
  path: string;
  label: PlainKey;
  body: PlainKey;
  icon: IconName;
  canonical: string;
  /** D-066: the sidebar group (a heading in the sidebar, a section bar on the page). */
  group: ConsoleGroup;
  /** Waits on a paid dependency (PAID_DEPENDENCIES): rendered as pending activation. */
  pendingActivation?: PlainKey;
}

export type ConsoleGroup = "main" | "money" | "services" | "platform" | "support";
/** Sidebar order of the groups; "main" has no heading. */
export const GROUPS: ConsoleGroup[] = ["main", "money", "services", "platform", "support"];

export const SECTIONS: ConsoleSection[] = [
  {
    id: "overview",
    group: "main",
    path: "/",
    label: "console.nav.overview",
    body: "console.sections.overview",
    icon: "home",
    canonical: "SYSTEM-001",
  },
  {
    id: "vendors",
    group: "main",
    path: "/vendors",
    label: "console.nav.vendors",
    body: "console.sections.vendors",
    icon: "store",
    canonical: "ORG-001, SYSTEM-001",
  },
  // D-065 (owner, 2026-10-25): the plans shops buy (price, icon, every feature, limits, credits).
  {
    id: "plans",
    group: "money",
    path: "/plans",
    label: "console.nav.plans",
    body: "console.sections.plans",
    icon: "crown",
    canonical: "BILLING-001",
  },
  {
    // D-064: the Adyvance Wallet console (top-ups, wallets, pack and theme prices, settings).
    id: "billing",
    group: "money",
    path: "/billing",
    label: "console.nav.billing",
    body: "console.sections.billing",
    icon: "wallet",
    canonical: "BILLING-001",
  },
  {
    // D-086: Adyvance's QR, bank and Fonepay QR, set up once and used by every service.
    id: "payments",
    group: "money",
    path: "/payments",
    label: "console.nav.payments",
    body: "console.sections.payments",
    icon: "bank",
    canonical: "BILLING-001",
  },
  // D-067 batch 2 (owner, 2026-10-25): every paid plan and trial, invoices and the renewal rule; plan
  // coupons and the shop referral programme.
  {
    id: "subscriptions",
    group: "money",
    path: "/subscriptions",
    label: "billingOps.nav.subscriptions",
    body: "billingOps.sections.subscriptions",
    icon: "receipt",
    canonical: "BILLING-001",
  },
  {
    id: "promos",
    group: "money",
    path: "/promos",
    label: "billingOps.nav.promos",
    body: "billingOps.sections.promos",
    icon: "percent",
    canonical: "BILLING-001, PARTNER-001",
  },
  // SERVICES-001 Import Marketplace (owner, 2026-10-23): vendors' wholesale orders, offers, requests.
  {
    id: "import",
    group: "services",
    path: "/import",
    label: "importMarket.console.title",
    body: "importMarket.console.lead",
    icon: "ship",
    canonical: "SERVICES-001",
  },
  // NOTIFY-001 SMS Service (owner, 2026-10-23; D-052): SMS credit purchases, balances, packs.
  {
    id: "sms",
    group: "services",
    path: "/sms",
    label: "smsService.console.title",
    body: "smsService.console.lead",
    icon: "chat",
    canonical: "NOTIFY-001",
  },
  // SERVICES-001 Digital Marketing ads (owner, 2026-10-23; D-053): shops' Meta and Google ads,
  // connections, results and wallet top-ups.
  {
    id: "ads",
    group: "services",
    path: "/ads",
    label: "ads.console.title",
    body: "ads.console.lead",
    icon: "megaphone",
    canonical: "SERVICES-001",
  },
  // SERVICES-001 Courses (owner, 2026-10-24; D-054): how-to videos shown on shops' Courses page.
  {
    id: "courses",
    group: "services",
    path: "/courses",
    label: "courses.console.title",
    body: "courses.console.lead",
    icon: "graduation",
    canonical: "SERVICES-001",
  },
  {
    id: "analytics",
    group: "platform",
    path: "/analytics",
    label: "console.nav.analytics",
    body: "console.sections.analytics",
    icon: "chart",
    canonical: "SYSTEM-001, ANALYTICS-001",
  },
  {
    id: "health",
    group: "platform",
    path: "/health",
    label: "console.nav.health",
    body: "console.sections.health",
    icon: "pulse",
    canonical: "SYSTEM-001, INTEGRATION-001",
  },
  {
    id: "capacity",
    group: "platform",
    path: "/capacity",
    label: "console.nav.capacity",
    body: "console.sections.capacity",
    icon: "gauge",
    canonical: "SYSTEM-001",
  },
  // D-067 batch 3 (owner, 2026-10-25): every store with its domains; the theme catalog.
  {
    id: "storefronts",
    group: "platform",
    path: "/storefronts",
    label: "platformOps.nav.storefronts",
    body: "platformOps.sections.storefronts",
    icon: "globe",
    canonical: "STORE-001",
  },
  {
    id: "themes",
    group: "platform",
    path: "/themes",
    label: "platformOps.nav.themes",
    body: "platformOps.sections.themes",
    icon: "palette",
    canonical: "THEME-001",
  },
  {
    id: "jobs",
    group: "platform",
    path: "/jobs",
    label: "console.nav.jobs",
    body: "console.sections.jobs",
    icon: "jobs",
    canonical: "SYSTEM-001",
  },
  {
    id: "config",
    group: "platform",
    path: "/config",
    label: "console.nav.config",
    body: "console.sections.config",
    icon: "sliders",
    canonical: "SYSTEM-001, BILLING-001",
  },
  {
    id: "releases",
    group: "platform",
    path: "/releases",
    label: "console.nav.releases",
    body: "console.sections.releases",
    icon: "rocket",
    canonical: "SYSTEM-001",
  },
  {
    id: "requests",
    group: "support",
    path: "/requests",
    label: "console.nav.requests",
    body: "console.sections.requests",
    icon: "inbox",
    canonical: "SYSTEM-001",
  },
  {
    id: "runner",
    group: "support",
    path: "/runner",
    label: "console.nav.runner",
    body: "console.sections.runner",
    icon: "terminal",
    canonical: "SYSTEM-001",
    pendingActivation: "console.runnerPending",
  },
  // D-067 batch 4 (owner, 2026-10-25): shoppers' reports about shops and the storefront take-down.
  {
    id: "reports",
    group: "support",
    path: "/reports",
    label: "trustOps.nav.reports",
    body: "trustOps.sections.reports",
    icon: "flag",
    canonical: "RISK-001",
  },
  {
    id: "security",
    group: "support",
    path: "/security",
    label: "console.nav.security",
    body: "console.sections.security",
    icon: "shield",
    canonical: "AUDIT-001, AUTH-001, SYSTEM-001",
  },
  {
    id: "support",
    group: "support",
    path: "/support",
    label: "console.nav.support",
    body: "console.sections.support",
    icon: "lifebuoy",
    canonical: "SUPPORT-001",
  },
];
